import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Users, MessageSquare, Mail, RefreshCw, Power, Copy, Eye, EyeOff, Settings, ArrowLeft } from 'lucide-react';
import { Navbar } from '../components/layout/Navbar';
import { Button } from '../components/ui/Button';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import { evolutionAPI } from '../utils/api';
interface WhatsAppStats {
  contacts: number | null;
  chats: number | null;
  messages: number | null;
}
export function WhatsAppDashboardPage() {
  const {
    user,
    updateUser
  } = useAuth();
  const {
    language,
    t
  } = useLanguage();
  const navigate = useNavigate();
  const isRTL = language === 'ar';
  const [instanceData, setInstanceData] = useState<any>(null);
  const [stats, setStats] = useState<WhatsAppStats>({
    contacts: null,
    chats: null,
    messages: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isRestarting, setIsRestarting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  useEffect(() => {
    if (!user?.instanceName) {
      navigate('/dashboard');
      return;
    }
    loadInstanceData();
  }, [user?.instanceName]);
  const loadInstanceData = async () => {
    if (!user?.instanceName) return;
    setIsLoading(true);
    try {
      // Fetch instance details from Evolution API
      const data = await evolutionAPI.fetchInstance(user.instanceName);
      console.log('Instance data:', data);
      setInstanceData(data);
      // Try to get real stats from Evolution API
      // Note: Evolution API might not provide these endpoints
      // If they don't exist, we'll leave them as null
      try {
        // Attempt to fetch real statistics if the API provides them
        // This is a placeholder - adjust based on actual Evolution API endpoints
        const statsData = {
          contacts: null,
          chats: null,
          messages: null // Evolution API doesn't provide this
        };
        setStats(statsData);
      } catch (statsError) {
        console.log('Stats not available from API');
        setStats({
          contacts: null,
          chats: null,
          messages: null
        });
      }
    } catch (error) {
      console.error('Error loading instance data:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleRestart = async () => {
    if (!user?.instanceName) return;
    setIsRestarting(true);
    try {
      // Restart instance logic would go here
      await evolutionAPI.connectInstance(user.instanceName);
      await new Promise(resolve => setTimeout(resolve, 2000));
      loadInstanceData();
    } catch (error) {
      console.error('Restart error:', error);
    } finally {
      setIsRestarting(false);
    }
  };
  const handleDisconnect = async () => {
    if (!user?.instanceName) return;
    const confirmed = window.confirm(isRTL ? 'هل أنت متأكد من فصل الاتصال؟ سيتم حذف جميع البيانات.' : 'Are you sure you want to disconnect? All data will be removed.');
    if (!confirmed) return;
    setIsDisconnecting(true);
    try {
      await evolutionAPI.deleteInstance(user.instanceName);
      updateUser({
        instanceName: undefined
      });
      navigate('/dashboard');
    } catch (error) {
      console.error('Disconnect error:', error);
    } finally {
      setIsDisconnecting(false);
    }
  };
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };
  const isConnected = instanceData?.instance?.state === 'open' || instanceData?.state === 'open';
  const phone = instanceData?.instance?.owner || instanceData?.owner || user?.phone;
  const profileName = instanceData?.instance?.profileName || instanceData?.profileName || user?.name;
  if (isLoading) {
    return <div className="min-h-screen bg-white flex items-center justify-center" data-id="element-3228">
        <div className="text-center" data-id="element-3229">
          <RefreshCw className="w-12 h-12 text-[#4f1964] animate-spin mx-auto mb-4" data-id="element-3230" />
          <p className="text-gray-600" data-id="element-3231">
            {isRTL ? 'جاري التحميل...' : 'Loading...'}
          </p>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-white" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-3232">
      <Navbar data-id="element-3233" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3234">
        {/* Back Button */}
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-6" data-id="element-3235">
          <ArrowLeft className="w-5 h-5" data-id="element-3236" />
          <span data-id="element-3237">{isRTL ? 'العودة إلى لوحة التحكم' : 'Back to Dashboard'}</span>
        </button>

        {/* Header Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6" data-id="element-3238">
          <div className="flex items-start justify-between mb-6" data-id="element-3239">
            <div data-id="element-3240">
              <h1 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-3241">
                {user?.instanceName}
              </h1>
              <p className="text-gray-500 text-sm" data-id="element-3242">
                {isRTL ? 'مساعد WhatsApp الذكي' : 'WhatsApp AI Assistant'}
              </p>
            </div>
            <div className={`px-4 py-2 rounded-full ${isConnected ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-600 border border-gray-200'}`} data-id="element-3243">
              <div className="flex items-center gap-2" data-id="element-3244">
                <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} data-id="element-3245"></span>
                <span className="text-sm font-medium" data-id="element-3246">
                  {isConnected ? isRTL ? 'متصل' : 'Connected' : isRTL ? 'غير متصل' : 'Disconnected'}
                </span>
              </div>
            </div>
          </div>

          {/* API Key Section (Hidden) */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200" data-id="element-3247">
            <div className="flex items-center justify-between" data-id="element-3248">
              <div className="flex-1" data-id="element-3249">
                <label className="text-xs text-gray-500 mb-1 block" data-id="element-3250">
                  {isRTL ? 'مفتاح API' : 'API Key'}
                </label>
                <div className="flex items-center gap-2" data-id="element-3251">
                  <code className="text-gray-700 font-mono text-sm" data-id="element-3252">
                    {showApiKey ? '68MlLUENzjjV142ye8r8NaLHONSvIABG' : '••••••••••••••••••••••••••••••••'}
                  </code>
                  <button onClick={() => setShowApiKey(!showApiKey)} className="text-gray-500 hover:text-gray-700 transition-colors" data-id="element-3253">
                    {showApiKey ? <EyeOff className="w-4 h-4" data-id="element-3254" /> : <Eye className="w-4 h-4" data-id="element-3255" />}
                  </button>
                  <button onClick={() => copyToClipboard('68MlLUENzjjV142ye8r8NaLHONSvIABG')} className="text-gray-500 hover:text-gray-700 transition-colors" data-id="element-3256">
                    <Copy className="w-4 h-4" data-id="element-3257" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Info */}
          <div className="flex items-center gap-4 mb-6" data-id="element-3258">
            <div className="w-16 h-16 bg-[#4f1964] rounded-full flex items-center justify-center text-white text-xl font-bold" data-id="element-3259">
              {profileName?.charAt(0).toUpperCase()}
            </div>
            <div data-id="element-3260">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-3261">
                {profileName}
              </h3>
              <p className="text-gray-600 text-sm" data-id="element-3262">
                {phone || (isRTL ? 'غير متوفر' : 'N/A')}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3" data-id="element-3263">
            <Button variant="outline" size="sm" onClick={handleRestart} isLoading={isRestarting} className="bg-white border-gray-300 text-gray-700 hover:bg-gray-50" data-id="element-3264">
              <RefreshCw className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-3265" />
              {isRTL ? 'إعادة التشغيل' : 'RESTART'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleDisconnect} isLoading={isDisconnecting} className="bg-white border-red-200 text-red-600 hover:bg-red-50" data-id="element-3266">
              <Power className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-3267" />
              {isRTL ? 'فصل الاتصال' : 'DISCONNECT'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/configure')} className="bg-white border-gray-300 text-gray-700 hover:bg-gray-50" data-id="element-3268">
              <Settings className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-3269" />
              {isRTL ? 'الإعدادات' : 'Settings'}
            </Button>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-3 gap-6" data-id="element-3270">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6" data-id="element-3271">
            <div className="flex items-center gap-4 mb-4" data-id="element-3272">
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center" data-id="element-3273">
                <Users className="w-6 h-6 text-blue-600" data-id="element-3274" />
              </div>
              <h3 className="text-gray-600 text-sm font-medium" data-id="element-3275">
                {isRTL ? 'جهات الاتصال' : 'Contacts'}
              </h3>
            </div>
            <p className="text-4xl font-bold text-gray-900" data-id="element-3276">
              {stats.contacts !== null ? stats.contacts.toLocaleString() : isRTL ? 'غير متوفر' : 'N/A'}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6" data-id="element-3277">
            <div className="flex items-center gap-4 mb-4" data-id="element-3278">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center" data-id="element-3279">
                <MessageSquare className="w-6 h-6 text-purple-600" data-id="element-3280" />
              </div>
              <h3 className="text-gray-600 text-sm font-medium" data-id="element-3281">
                {isRTL ? 'المحادثات' : 'Chats'}
              </h3>
            </div>
            <p className="text-4xl font-bold text-gray-900" data-id="element-3282">
              {stats.chats !== null ? stats.chats.toLocaleString() : isRTL ? 'غير متوفر' : 'N/A'}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6" data-id="element-3283">
            <div className="flex items-center gap-4 mb-4" data-id="element-3284">
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center" data-id="element-3285">
                <Mail className="w-6 h-6 text-green-600" data-id="element-3286" />
              </div>
              <h3 className="text-gray-600 text-sm font-medium" data-id="element-3287">
                {isRTL ? 'الرسائل' : 'Messages'}
              </h3>
            </div>
            <p className="text-4xl font-bold text-gray-900" data-id="element-3288">
              {stats.messages !== null ? stats.messages.toLocaleString() : isRTL ? 'غير متوفر' : 'N/A'}
            </p>
          </motion.div>
        </div>

        {/* Info Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="mt-6 bg-white rounded-2xl border border-gray-200 shadow-sm p-6" data-id="element-3289">
          <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3290">
            {isRTL ? 'معلومات الاتصال' : 'Connection Info'}
          </h3>
          <div className="grid md:grid-cols-2 gap-4" data-id="element-3291">
            <div data-id="element-3292">
              <label className="text-xs text-gray-500 mb-1 block" data-id="element-3293">
                {isRTL ? 'اسم Instance' : 'Instance Name'}
              </label>
              <p className="text-gray-900 font-medium" data-id="element-3294">{user?.instanceName}</p>
            </div>
            <div data-id="element-3295">
              <label className="text-xs text-gray-500 mb-1 block" data-id="element-3296">
                {isRTL ? 'الحالة' : 'Status'}
              </label>
              <p className={isConnected ? 'text-green-600 font-medium' : 'text-gray-600'} data-id="element-3297">
                {isConnected ? isRTL ? 'نشط ومتصل' : 'Active & Connected' : isRTL ? 'غير متصل' : 'Disconnected'}
              </p>
            </div>
            <div data-id="element-3298">
              <label className="text-xs text-gray-500 mb-1 block" data-id="element-3299">
                {isRTL ? 'رقم الهاتف' : 'Phone Number'}
              </label>
              <p className="text-gray-900 font-medium" data-id="element-3300">
                {phone || (isRTL ? 'غير متوفر' : 'N/A')}
              </p>
            </div>
            <div data-id="element-3301">
              <label className="text-xs text-gray-500 mb-1 block" data-id="element-3302">
                {isRTL ? 'اسم الملف الشخصي' : 'Profile Name'}
              </label>
              <p className="text-gray-900 font-medium" data-id="element-3303">
                {profileName || (isRTL ? 'غير متوفر' : 'N/A')}
              </p>
            </div>
          </div>
        </motion.div>
      </main>
    </div>;
}